import { createContext, useContext, useEffect, useState } from 'react';
import { getPublicErrorMessage } from '../publicError';
import { supabaseClient } from '../supabase';
import { useAuth } from './AuthProvider';
import { useLanguage } from './LanguageProvider';

const ContactFormContext = createContext(null);

function userContact(user) {
  return {
    name: user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '',
    email: user?.email || '',
    phone: user?.phone || user?.user_metadata?.phone || '',
  };
}

function ContactFormModal({ closeContact, isOpen, submitContact, submitting, user }) {
  const { t } = useLanguage();
  const contact = userContact(user);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeContact();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [closeContact, isOpen]);

  if (!isOpen) return null;

  const submit = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    submitContact({
      name: form.get('name').trim(),
      email: form.get('email').trim(),
      phone: form.get('phone').trim(),
      subject: form.get('subject').trim(),
      message: form.get('message').trim(),
    });
  };

  return (
    <div className="quote-overlay" onClick={(event) => event.target === event.currentTarget && closeContact()} role="presentation">
      <div aria-labelledby="contactFormTitle" aria-modal="true" className="quote-contact-modal contact-form-modal" role="dialog">
        <div className="quote-panel-header">
          <div>
            <h2 id="contactFormTitle">{t('Contact Us', 'اتصل بنا')}</h2>
            <p>{t('Send us a message and our team will contact you shortly.', 'أرسل لنا رسالة وسيتواصل معك فريقنا قريبًا.')}</p>
          </div>
          <button aria-label={t('Close contact form', 'إغلاق نموذج التواصل')} className="quote-close" onClick={closeContact} type="button">&times;</button>
        </div>
        <form onSubmit={submit}>
          <div className="contact-form-fields">
            <div className="form-group">
              <label htmlFor="contactName">{t('Full Name', 'الاسم الكامل')}</label>
              <input autoComplete="name" defaultValue={contact.name} id="contactName" name="name" required type="text" />
            </div>
            <div className="form-group">
              <label htmlFor="contactEmail">{t('Email', 'البريد الإلكتروني')}</label>
              <input autoComplete="email" defaultValue={contact.email} id="contactEmail" name="email" required type="email" />
            </div>
            <div className="form-group">
              <label htmlFor="contactPhone">{t('Phone Number', 'رقم الهاتف')}</label>
              <input autoComplete="tel" defaultValue={contact.phone} id="contactPhone" name="phone" type="tel" />
            </div>
            <div className="form-group">
              <label htmlFor="contactSubject">{t('Subject', 'الموضوع')}</label>
              <input id="contactSubject" name="subject" required type="text" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="contactMessage">{t('Message', 'الرسالة')}</label>
            <textarea id="contactMessage" name="message" required rows="5" />
          </div>
          <button className="btn2 quote-checkout-button" disabled={submitting} type="submit">
            {submitting ? t('Sending...', 'جاري الإرسال...') : t('Send Message', 'إرسال الرسالة')}
          </button>
        </form>
      </div>
    </div>
  );
}

export function ContactFormProvider({ children }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const submitContact = async (contact) => {
    setSubmitting(true);

    try {
      const { error } = await supabaseClient.from('contact_messages').insert({
        customer_name: contact.name,
        customer_email: contact.email,
        customer_phone: contact.phone || null,
        subject: contact.subject,
        message: contact.message,
        source_path: window.location.pathname,
      });

      if (error) throw error;

      const { data: emailResult, error: emailError } = await supabaseClient.functions.invoke('send-contact-email', {
        body: {
          ...contact,
          sourcePath: window.location.pathname,
        },
      });

      if (emailError || !emailResult?.emailed) {
        setIsOpen(false);
        setNotice({
          type: 'error',
          text: t(
            'Your message was saved, but the email notification could not be sent.',
            'تم حفظ رسالتك، ولكن تعذر إرسال إشعار البريد الإلكتروني.',
          ),
        });
        return;
      }
    } catch (error) {
      setNotice({
        type: 'error',
        text: getPublicErrorMessage(error, {
          network: t('Please check your connection and try again.', 'يرجى التحقق من الاتصال والمحاولة مرة أخرى.'),
        }, t('Your message could not be sent. Please try again.', 'تعذر إرسال رسالتك. يرجى المحاولة مرة أخرى.')),
      });
      return;
    } finally {
      setSubmitting(false);
    }

    setIsOpen(false);
    setNotice({ type: 'success', text: t('Your message was saved and emailed.', 'تم حفظ رسالتك وإرسالها عبر البريد الإلكتروني.') });
  };

  return (
    <ContactFormContext.Provider value={{ openContact: () => setIsOpen(true) }}>
      {children}
      <ContactFormModal
        closeContact={() => setIsOpen(false)}
        isOpen={isOpen}
        submitContact={submitContact}
        submitting={submitting}
        user={user}
      />
      {notice && <div className={`quote-notice ${notice.type}`} role="status">{notice.text}</div>}
    </ContactFormContext.Provider>
  );
}

export function useContactForm() {
  const context = useContext(ContactFormContext);
  if (!context) throw new Error('useContactForm must be used inside ContactFormProvider');
  return context;
}
